import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import minMax from 'dayjs/plugin/minMax';

import { DATE_ID_FORMAT } from '@/helpers';
import { mapTeamMemberStatusFromDTO } from '@/modules/common/mappers/team-member-status.mapper';
import { MemberStatus } from '@/modules/team';
import { logger } from '@/utils/logger/logger';

import { IDLE_CALLBACK_TIMEOUT_MS, PLANNER_DATA_LOG_TAG } from '../../constants';
import { calculateCellWidth, getStateChangeForDate } from '../../helpers';
import { getTimelineRowHeight } from '../../helpers/planner-base/timeline-row-height';
import { DEFAULT_CAPACITY_MINUTES } from '../../helpers/time-off-day-stacking';
import { useAssignmentLevels, useVisibleDateWindow } from '../../hooks';
import usePlannerStore from '../../store';
import { AssignmentType, RowItem, RowItemType, TimeOffBlockType } from '../../types';
import { StickyAssignmentLabels } from '../assignment/text/sticky-assignment-labels';
import { StickyLabelsProvider } from '../assignment/text/sticky-labels-context';
import { StickyTimeOffLabelsProvider } from '../time-off/text/sticky-time-off-labels-context';
import { TimelineCellShell } from './timeline-cell-shell';
import { TimelineRowOverlay } from './timeline-row-overlay';
import { TimelineRowSidebar } from './timeline-row-sidebar';
import { TimelineRowSidebarSkeleton } from './timeline-row-sidebar-skeleton';
import { TimelineSpacer } from './timeline-spacer';

dayjs.extend(minMax);
dayjs.extend(isSameOrAfter);

interface TimelineRowProps {
  row: RowItem;
}

const EMPTY_ASSIGNMENTS: AssignmentType[] = [];
const EMPTY_TIME_OFFS: TimeOffBlockType[] = [];

/**
 * Two-phase row rendering:
 *
 * Phase 1 (synchronous mount) — only subscribes to store values needed for
 * the lightweight grid shell: dates, zoom, collapse state, assignments (for
 * row height), and visible-date window. Renders TimelineCellShell divs and a
 * skeleton sidebar.
 *
 * Phase 2 (deferred via requestIdleCallback) — mounts TimelineRowOverlay
 * which subscribes to heavier store values (currentStateChange,
 * utilizationBasedOn) and renders full PlannerTeamMemberInfo, utilization
 * cells with tooltips, assignments, phantom assignments, and state change
 * indicators.
 */
export const TimelineRow = memo(({ row }: TimelineRowProps) => {
  // --- Phase 1 store subscriptions (needed for shell layout) ---
  const dates = usePlannerStore(store => store.dates);
  const visibleDateRange = usePlannerStore(store => store.visibleDateRange);
  const zoomLevel = usePlannerStore(store => store.zoomLevel);
  const assignments = usePlannerStore(store => store.assignments.get(row.id) ?? EMPTY_ASSIGNMENTS);
  const timeOffs = usePlannerStore(store => store.timeOffs.get(row.id) ?? EMPTY_TIME_OFFS);
  const userInfoVisibility = usePlannerStore(store => store.userInfoVisibility);
  const isParentCollapsed = usePlannerStore(store =>
    row.parentGroupId ? store.collapsedGroups.has(row.parentGroupId) : false
  );

  const userStartDate = row.stateChanges?.[0]?.isInitialChange
    ? dayjs(row.stateChanges[0].effectiveStartDate)
    : undefined;

  // Diagnostic: surface the "assignments missing after vertical pagination" case.
  // Flags when this rendered row shows no assignments while the store actually holds
  // some for this exact row id (i.e. the selector returned stale-empty). Disabled by
  // default via the PlannerData log tag; enable it to capture/share logs if it recurs.
  useEffect(() => {
    if (row.type !== RowItemType.User) return;
    if (assignments.length > 0) return;
    const stored = usePlannerStore.getState().assignments.get(row.id);
    if (stored && stored.length > 0) {
      logger.debug(
        PLANNER_DATA_LOG_TAG,
        'row rendered EMPTY but store has assignments rowId=' +
          row.id +
          ' storedCount=' +
          stored.length
      );
    }
  }, [row.id, row.type, assignments]);

  // Assignment levels needed for correct row height from mount
  const { assignmentLevels, maxIntersectedAssignments } = useAssignmentLevels(assignments);

  const cellWidth = calculateCellWidth(zoomLevel);

  // Time-off never grows the row: a block always fits within the existing content
  // area (sized as a portion of it) and a partial time-off repositions the
  // assignment into the free zone beside the block. Row height depends only on
  // assignment levels and the sidebar header.
  const cellHeight = getTimelineRowHeight({
    maxIntersectedAssignments,
    userInfoVisibility,
  });
  const { visibleDates, leftSpacerWidth, rightSpacerWidth } = useVisibleDateWindow({
    dates,
    visibleDateRange,
    cellWidth,
  });

  // --- Phase 1: Lightweight grid shell (synchronous) ---
  const shellCells = useMemo(
    () =>
      visibleDates.map(date => {
        const isUserCreated = !userStartDate || date.isSameOrAfter(userStartDate, 'day');
        const dateStateChange = getStateChangeForDate(row.stateChanges, date);
        const dateStatus = dateStateChange?.displayStatus
          ? mapTeamMemberStatusFromDTO(dateStateChange.displayStatus)
          : MemberStatus.Basic;
        const isArchived = dateStatus === MemberStatus.Archived;

        return (
          <TimelineCellShell
            key={`shell-${date.format(DATE_ID_FORMAT)}`}
            date={date}
            cellWidth={cellWidth}
            height={cellHeight}
            disabled={!isUserCreated || isArchived}
          />
        );
      }),
    [visibleDates, row.stateChanges, userStartDate, cellWidth, cellHeight]
  );

  // Track which assignment fragments have sticky labels active (text fits within bar)
  const [stickyFragmentsByAssignment, setStickyFragmentsByAssignment] = useState<
    Map<number, Set<string>>
  >(() => new Map());
  const stickyContextValue = useMemo(
    () => ({ stickyFragmentsByAssignment }),
    [stickyFragmentsByAssignment]
  );
  const handleStickyFragmentsChange = useCallback((fragments: Map<number, Set<string>>) => {
    setStickyFragmentsByAssignment(fragments);
  }, []);

  // Track which time-off segments have sticky labels active (label fits the bar)
  const [stickySegmentsByTimeOff, setStickySegmentsByTimeOff] = useState<Map<number, Set<string>>>(
    () => new Map()
  );
  const stickyTimeOffContextValue = useMemo(
    () => ({ stickySegmentsByTimeOff }),
    [stickySegmentsByTimeOff]
  );
  const handleStickyTimeOffSegmentsChange = useCallback((segments: Map<number, Set<string>>) => {
    setStickySegmentsByTimeOff(segments);
  }, []);

  // Per-date capacity resolver for time-off segment layout (matches overlay).
  // Falls back to the member's nominal day when the utilization capacity is
  // missing OR zero (a full-vacation day can report capacity 0).
  const getCapacityMinutes = useCallback(
    (dateKey: string) => {
      const nominal = row.hoursPerDay > 0 ? row.hoursPerDay * 60 : DEFAULT_CAPACITY_MINUTES;
      const capacity = row.utilizationByDate?.get(dateKey)?.capacity;
      return capacity && capacity > 0 ? capacity : nominal;
    },
    [row.utilizationByDate, row.hoursPerDay]
  );

  // --- Phase 2: Heavy interactive content (deferred) ---
  const [isContentReady, setIsContentReady] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(
        () => {
          if (mountedRef.current) setIsContentReady(true);
        },
        { timeout: IDLE_CALLBACK_TIMEOUT_MS }
      );
      return () => window.cancelIdleCallback(id);
    }

    let outer = 0;
    let inner = 0;
    outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        if (mountedRef.current) setIsContentReady(true);
      });
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, []);

  // Don't render if parent group is collapsed
  if (isParentCollapsed) {
    return null;
  }

  return (
    <StickyLabelsProvider value={stickyContextValue}>
      <StickyTimeOffLabelsProvider value={stickyTimeOffContextValue}>
        <div className="relative flex min-w-fit">
          {/* Column Header — skeleton during phase 1, full info in phase 2 */}
          <div className="sticky left-0 top-0 z-10 flex w-56 min-w-[14rem] items-start gap-2 border-b border-r bg-background p-4">
            {isContentReady ? (
              <TimelineRowSidebar row={row} userStartDate={userStartDate} />
            ) : (
              <TimelineRowSidebarSkeleton />
            )}
          </div>
          <div className="relative flex flex-1 select-none overflow-clip">
            <TimelineSpacer widthRem={leftSpacerWidth} height={cellHeight} />
            {shellCells}
            <TimelineSpacer widthRem={rightSpacerWidth} height={cellHeight} />
            {isContentReady && (
              <>
                <TimelineRowOverlay
                  row={row}
                  assignments={assignments}
                  timeOffs={timeOffs}
                  assignmentLevels={assignmentLevels}
                  cellWidth={cellWidth}
                  cellHeight={cellHeight}
                  userStartDate={userStartDate}
                  dates={dates}
                  visibleDates={visibleDates}
                  leftSpacerWidth={leftSpacerWidth}
                  rightSpacerWidth={rightSpacerWidth}
                  onStickyTimeOffSegmentsChange={handleStickyTimeOffSegmentsChange}
                />
                <StickyAssignmentLabels
                  assignments={assignments}
                  timeOffs={timeOffs}
                  assignmentLevels={assignmentLevels}
                  cellWidth={cellWidth}
                  cellHeight={cellHeight}
                  getCapacityMinutes={getCapacityMinutes}
                  dates={dates}
                  onStickyFragmentsChange={handleStickyFragmentsChange}
                />
              </>
            )}
          </div>
        </div>
      </StickyTimeOffLabelsProvider>
    </StickyLabelsProvider>
  );
});
